<template>
  <div class="invoices-container d-flex flex-column flex-grow-1">
    <b-row class="flex-grow-1 d-flex h-100">
      <b-col
        :md="9"
        class="flex-grow-1 d-flex flex-column pb-4 invoice-list-col"
        :order="1"
        :order-md="0"
      >
        <drip-card v-if="alertFilters" class="alert-filter-card">
          {{
            $t('invoices.index.require.text', {
              filter: $t(
                'invoices.index.require.' +
                  (alertFilters[0] === 'basic_info_pending'
                    ? 'information'
                    : 'documents')
              )
            })
          }}
          <button class="clear-button" @click.prevent="setFilters(null)">
            <b-icon-x />
          </button>
        </drip-card>
        <drip-search-form v-model="searchTerm" v-if="!alertFilters" />
        <drip-card class="flex-grow-1 invoice-list-card d-flex flex-column">
          <invoices-filters
            v-model="filters"
            ref="invoiceFilter"
            v-if="searchTerm === '' && !alertFilters"
            :invoice-meta="invoiceMeta"
          />
          <invoices-table
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :invoice-meta="invoiceMeta"
            :invoices="invoices"
            :pagination="paginationVars"
            :current-page="currentPage"
            :monitoringData="monitoringData"
            :loading="loading"
            @scrollBottom="loadNextPage"
            @refreshInvoices="refreshInvoices"
          />
        </drip-card>
      </b-col>
      <b-col
        :md="3"
        class="alerts-list-col d-flex flex-column-reverse d-md-block"
        :order="0"
        :order-md="1"
      >
        <data-update-alert-box
          v-if="showMonitoringAlert"
          :date="monitoringData.revoke_date"
          extraClass="flex-column"
        />
        <export-data />
        <invoice-list-alerts
          class="mb-3"
          @filter-invoices="setFilters"
          :customer-pending-tasks="pendingTasks"
        />
      </b-col>
    </b-row>
  </div>
</template>
<script>
import invoiceService from '@/services/invoice'
import exportersService from '@/services/exporters'
import DripCard from '../../components/drip/ui/Card'
import DripSearchForm from '../../components/drip/form/SearchForm'
import InvoicesTable from '../../components/invoices/list/InvoicesTable'
import ExportData from '../../components/invoices/list/ExportData'
import InvoicesFilters from '../../components/invoices/list/InvoicesFilters'
import InvoiceListAlerts from '../../components/invoices/list/Alerts'
import transactionService from '@/services/transactions'
import DataUpdateAlertBox from '@/components/common/DataUpdateAlertBox'
import monitoringService from '@/services/monitoring'

export default {
  components: {
    DripSearchForm,
    InvoicesFilters,
    InvoicesTable,
    DripCard,
    ExportData,
    InvoiceListAlerts,
    DataUpdateAlertBox
  },
  data() {
    return {
      sortBy: 'created_at',
      sortDesc: true,
      searchTerm: '',
      invoices: [],
      loading: false,
      pageLoading: false,
      monitoringData: null,
      filters: this.$route.params.selectedFilter
        ? [this.$route.params.selectedFilter]
        : ['processing', 'approved', 'pending'],
      currentPage: 1,
      paginationVars: {
        per_page: 20,
        total_objects: 8,
        total_pages: 1
      },
      invoiceMeta: null,
      pendingTasks: [],
      alertFilters: null
    }
  },
  mounted() {
    document.title = 'Invoices | Drip Portal'
    this.$mpTrack('Invoices page mounted')
    this.fetchMonitoringData()
    this.fetchInvoices()
    this.loadCounters()
    this.loadPendingTasks()
    this.setAlertFilters()
  },
  computed: {
    showMonitoringAlert() {
      if (
        !!this.monitoringData &&
        !!this.monitoringData?.mail_date &&
        this.$moment(this.monitoringData?.revoke_date).isValid() &&
        this.$moment(this.monitoringData?.mail_date).isValid() &&
        (!this.monitoringData.financials.active ||
          !this.monitoringData?.gst.active)
      ) {
        const mailDate = new Date(this.monitoringData?.mail_date) // Convert to Date object
        const currentDate = new Date() // Get today's date

        // Calculate the date 45 days after the mail_date
        const days30AfterMailDate = new Date(mailDate)
        days30AfterMailDate.setDate(mailDate.getDate() + 30)

        // Check if today's date is past the 45-day mark
        return (
          currentDate > days30AfterMailDate ||
          currentDate > new Date(this.monitoringData?.revoke_date)
        )
      }
      return false
    },
    sortParams() {
      // for importers added user_id for sorting api to work
      if (!this.sortBy) {
        return ''
      }
      if (this.sortBy.includes('importer')) {
        return this.sortBy + '_user_id' + (this.sortDesc ? ' desc' : ' asc')
      }
      return this.sortBy + (this.sortDesc ? ' desc' : ' asc')
    },
    hasPages() {
      return this.paginationVars.currentPage < this.paginationVars.totalPages
    }
  },
  methods: {
    refreshInvoices() {
      this.fetchInvoices()
    },
    setFilters(newFilters) {
      this.alertFilters = newFilters
      this.fetchInvoices()
    },
    setAlertFilters() {
      return this.$route.params.alertFilters
        ? this.setFilters(this.$route.params.alertFilters)
        : false
    },
    loadPendingTasks() {
      return exportersService
        .fetchPendingTasks(this.$store.state.account.accountId)
        .then((res) => {
          this.pendingTasks = res.data.customer_pending_task_flows
        })
        .catch(console.error)
    },
    loadNextPage() {
      this.currentPage++
      this.fetchInvoices(true)
    },
    loadCounters() {
      return exportersService
        .getInvoiceMeta(this.$store.state.account.accountId)
        .then((res) => {
          this.invoiceMeta = res.data
        })
        .catch(console.error)
    },
    fetchInvoiceQuery() {
      if (this.alertFilters) {
        return invoiceService.fetch(
          this.$store.state.account.accountId,
          {
            filters: this.alertFilters,
            sort: this.sortParams
          },
          this.currentPage,
          this.paginationVars.per_page,
          'Business::InvoiceListSerializer'
        )
      } else if (this.searchTerm !== '') {
        return invoiceService.fetch(
          this.$store.state.account.accountId,
          {
            search: this.searchTerm,
            sort: this.sortParams
          },
          this.currentPage,
          this.paginationVars.per_page,
          'Business::InvoiceListSerializer'
        )
      }
      return invoiceService.fetch(
        this.$store.state.account.accountId,
        {
          filters: this.filters,
          sort: this.sortParams
        },
        this.currentPage,
        this.paginationVars.per_page,
        'Business::InvoiceListSerializer'
      )
    },
    fetchInvoices(append = false) {
      if (!append) {
        this.loading = true
      }
      return this.fetchInvoiceQuery()
        .then(async (res) => {
          if (res && res.data) {
            const invoiceData = res.data['drip/business/invoices']
            const resData = await invoiceService.checkSwiftCopy(
              'invoices',
              invoiceData.map((x) => x.invoice_id || x.id)
            )
            const resData2 = await invoiceService.checkNocCopy(
              'invoices',
              invoiceData.map((x) => x.invoice_id || x.id)
            )
            if (resData.data) {
              Object.keys(resData.data).forEach((v) => {
                const index = invoiceData.findIndex((x) => x.id === parseInt(v))
                if (resData.data[v]) {
                  invoiceData[index].swiftAvail =
                    resData.data[v].advances ||
                    resData.data[v].settlement ||
                    false
                  invoiceData[index].swiftType = resData.data[v].advances
                    ? 'advance'
                    : resData.data[v].settlement
                    ? 'settlement'
                    : 'all'
                }
              })
            }
            if (resData2.data) {
              Object.keys(resData2.data).forEach((v) => {
                const index = invoiceData.findIndex((x) => x.id === parseInt(v))
                if (resData2.data[v]) {
                  invoiceData[index].nocAvail =
                    resData2.data[v].advances ||
                    resData2.data[v].settlement ||
                    false
                  invoiceData[index].swiftType = resData2.data[v].advances
                    ? 'advance'
                    : resData2.data[v].settlement
                    ? 'settlement'
                    : 'all'
                }
              })
            }
            const newInvoices = this.filters.includes('advanced')
              ? await this.appendAmountInInrValue(invoiceData)
              : invoiceData

            this.paginationVars = res.data.meta.pagination
            if (append) {
              this.invoices = JSON.parse(
                JSON.stringify(this.invoices.concat(newInvoices))
              )
            } else {
              this.invoices = newInvoices
            }
          }
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
        })
    },
    async appendAmountInInrValue(invoices) {
      const idsToFetch = []
      invoices.forEach((invoice) => {
        /**
         * this should append value in case of advanced
         * else return the invoice whitout doing any api call
         */
        if (invoice.status === 'open' && invoice.advances.length === 1) {
          idsToFetch.push(invoice.id)
        }
      })
      const dataToSend = {
        type: 'invoice',
        attribute_ids: idsToFetch
      }
      const { data: invoiceDataWithTranxnValue } =
        await transactionService.fetchTransactionsConversionRates(
          this.$store.getters.user.id,
          dataToSend
        )
      const mergedWithInvoiceData = invoices.map((invoice) => ({
        ...invoice,
        ...invoiceDataWithTranxnValue.find(
          (invWithTrxcn) => invWithTrxcn.invoice_id === invoice.id
        )
      }))
      return mergedWithInvoiceData
    },
    async fetchMonitoringData() {
      this.loading = true
      try {
        const res = await monitoringService.getMonitoringData(
          this.$store.getters.accountId
        )
        this.monitoringData = res.data
      } catch (error) {
        console.error('Error fetching monitoring data:', error)
        // Optionally set an error message to display to the user
        this.errorMessage = 'Failed to fetch monitoring data. Please try again.'
      } finally {
        this.loading = false // Ensure loading is set to false regardless of success or error
      }
    }
  },
  watch: {
    sortBy: function () {
      this.currentPage = 1
      this.fetchInvoices()
    },
    sortDesc: function () {
      this.currentPage = 1
      this.fetchInvoices()
    },
    filters: function () {
      this.alertFilters = null
      this.currentPage = 1
      this.fetchInvoices()
    },
    searchTerm: function () {
      this.alertFilters = null
      this.currentPage = 1
      this.fetchInvoices()
    }
  }
}
</script>

<style lang="scss">
.invoices {
  .dashboard-main-container {
    flex-direction: column;
    display: flex;
    flex: 1;
  }
  .dashboard-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
}

.invoices-container {
  align-self: stretch;
}

.clear-button {
  background: transparent;
  position: absolute;
  border: 0;
  cursor: pointer;
  width: 50px;
  right: 0;
  z-index: 100;
  cursor: pointer;
  &:focus {
    outline: 0;
  }
}
.invoice-list-card {
  height: 80vh;
  & > .card-body {
    max-height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.invoice-section-title {
  font-size: 16px;
  margin-bottom: 20px;
}

.invoice-list-col {
  height: 100%;
}

.alerts-list-col {
  max-height: 100%;
}

.filter-cancel {
  float: right;
}

.alert-filter-card.drip-card.card {
  margin-bottom: 24px;
  .card-body {
    padding: 20px 15px;
  }
}
</style>
<style scoped lang="scss">
:deep(.account-title) {
  font-size: 16px !important;
}
:deep(.account-sub) {
  font-size: 14px !important;
}
:deep(.btn-alert) {
  width: 75%;
  margin-top: 24px;
}
:deep(.icon-alert) {
  font-size: 20px !important;
}
</style>
